import React, { useState, useEffect, useCallback, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
// import "pdfjs-dist/build/pdf.worker.entry";
import moment from 'moment';
import PersonAdd from '@mui/icons-material/PersonAdd';
import FilePresent from "@mui/icons-material/FilePresent";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import { ModalAddReceiver } from "./ModalAddReceiver";
import { useManageDigitalSignature } from '../../../hooks/useManageDigitalSignature';
import '../../../styles/components/_createsignature.scss';
import { Card, CardContent } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import PersonIcon from '@mui/icons-material/Person';

export function FileManageSignature() {
    const fileInputRef = useRef(null);
    const [digitalSignature] = useManageDigitalSignature();
    const {
        HandleSave,
        SendSignatureToEmail
    } = digitalSignature;
    const [open, setOpen] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);
    const [selectedRecipient, setSelectedRecipient] = useState("");
    const [backgroundImages, setBackgroundImages] = useState([]);
    const [backgroundSize, setBackgroundSize] = useState({ width: 0, height: 0 });
    const [predesignedObjects, setPredesignedObjects] = useState([]);
    const [draggingObjectId, setDraggingObjectId] = useState(null);
    const [resizingObjectId, setResizingObjectId] = useState(null);
    const [mouseStart, setMouseStart] = useState({ x: 0, y: 0 });
    const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 });
    const [originalPosition, setOriginalPosition] = useState({ x: 0, y: 0 });
    const [selectedObjectId, setSelectedObjectId] = useState(null);
    const [recipients, setRecipients] = useState([]);

    const onClose = () => setOpen(false);
    const handleFileClick = () => fileInputRef.current.click();

    const OnSetRecipientsAndSelect = (dto) => {
        setRecipients(dto);
        setSelectedRecipient(dto[0].email);
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === "application/pdf")
            setPdfFile(file);
    };

    const handlePdfUpload = useCallback(() => {
        if (pdfFile && pdfFile.type === "application/pdf") {
            const fileReader = new FileReader();

            fileReader.onload = async () => {
                const pdfData = new Uint8Array(fileReader.result);
                const pdf = await pdfjsLib.getDocument(pdfData).promise;
                const totalPages = pdf.numPages;
                const images = [];

                for (let i = 1; i <= totalPages; i++) {
                    const page = await pdf.getPage(i);
                    const viewport = page.getViewport({ scale: 1.5 });
                    const canvas = document.createElement("canvas");
                    const context = canvas.getContext("2d");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({ canvasContext: context, viewport }).promise;
                    const url_page = canvas.toDataURL();
                    images.push({ url_page, viewport, order: i.toString(), image_size_width: viewport.width, image_size_height: viewport.height });
                }

                setBackgroundImages(images);
                if (totalPages > 0)
                    setBackgroundSize({ width: images[0].viewport.width, height: images[0].viewport.height });
            };
            fileReader.readAsArrayBuffer(pdfFile);
        }
    }, [pdfFile]);

    const handleMouseMove = (event) => {
        if (draggingObjectId !== null) {
            const dx = event.clientX - mouseStart.x;
            const dy = event.clientY - mouseStart.y;

            const updatedObjects = predesignedObjects.map((obj) => {
                if (obj.id_signature === draggingObjectId) {
                    return {
                        ...obj,
                        pixel_x: Math.max(0, Math.min(originalPosition.x + dx, backgroundSize.width - obj.width)),
                        pixel_y: Math.max(0, Math.min(originalPosition.y + dy, backgroundSize.height - obj.height)),
                    };
                }
                return obj;
            });
            setPredesignedObjects(updatedObjects);
        } else if (resizingObjectId !== null) {
            const dx = event.clientX - mouseStart.x;
            const dy = event.clientY - mouseStart.y;

            const updatedObjects = predesignedObjects.map((obj) => {
                if (obj.id_signature === resizingObjectId) {
                    return {
                        ...obj,
                        width: Math.max(20, originalSize.width + dx),
                        height: Math.max(20, originalSize.height + dy),
                    };
                }
                return obj;
            });
            setPredesignedObjects(updatedObjects);
        }
    };

    const handleMouseUp = () => {
        setResizingObjectId(null);
        setDraggingObjectId(null);
    };

    const handleDragStart = (type, id = null) => {
        if (id) {
            setDraggingObjectId(id);
            const obj = predesignedObjects.find(o => o.id_signature === id);
            if (obj) {
                setOriginalPosition({ x: obj.pixel_x, y: obj.pixel_y });
                setSelectedObjectId(id);
            }
        } else {
            const newObject = {
                id_signature: Date.now().toString(),
                type: type,
                receiver_email: selectedRecipient || (recipients.length > 0 ? recipients[0].email : ""),
                width: 120,
                height: 50,
                pixel_x: 0,
                pixel_y: 0,
                date_time: moment().format()
            };
            setPredesignedObjects([...predesignedObjects, newObject]);
            setDraggingObjectId(newObject.id_signature);
            setSelectedObjectId(newObject.id_signature);
        }
    };

    const handleDeleteBox = () => {
        setPredesignedObjects(predesignedObjects.filter(obj => obj.id_signature !== selectedObjectId));
        setSelectedObjectId(null);
    };

    const handleDrop = (event, index) => {
        event.preventDefault();
        if (draggingObjectId !== null) {
            const updatedObjects = predesignedObjects.map((obj) => {
                if (obj.id_signature === draggingObjectId) {
                    return {
                        ...obj,
                        pixel_x: Math.min(event.nativeEvent.offsetX, backgroundSize.width - obj.width),
                        pixel_y: Math.min(event.nativeEvent.offsetY, backgroundSize.height - obj.height),
                        pageIndex: index
                    };
                }
                return obj;
            });
            setPredesignedObjects(updatedObjects);
            setDraggingObjectId(null);
            setSelectedObjectId(null);
        }
    };

    const handleDragOver = (event) => event.preventDefault();

    const handleMouseDownResize = (id, event) => {
        event.stopPropagation();
        setResizingObjectId(id);
        setMouseStart({ x: event.clientX, y: event.clientY });
        const obj = predesignedObjects.find(o => o.id_signature === id);
        if (obj)
            setOriginalSize({ width: obj.width, height: obj.height });
    };

    const GetIcon = (type) => {
        switch (type) {
            case "Nombre":
                return <PersonIcon color="#040404" sx={{ fontSize: 20 }} />;
            case "Sello":
                return <TextSnippetIcon color="#040404" sx={{ fontSize: 20 }} />;
            default:
                return <DownloadIcon color="#040404" sx={{ fontSize: 20 }} />;
        }
    }

    useEffect(() => {
        handlePdfUpload();
    }, [pdfFile, handlePdfUpload]);

    const handleFinalize = () => {
        HandleSave(pdfFile, backgroundImages, predesignedObjects, recipients);
    }

    const handleEmail = () => {
        SendSignatureToEmail();
        resetStates();
    }

    const resetStates = () => {
        setOpen(false);
        setPdfFile(null);
        setSelectedRecipient("");
        setBackgroundImages([]);
        setBackgroundSize({ width: 0, height: 0 });
        setPredesignedObjects([]);
        setDraggingObjectId(null);
        setResizingObjectId(null);
        setMouseStart({ x: 0, y: 0 });
        setOriginalSize({ width: 0, height: 0 });
        setOriginalPosition({ x: 0, y: 0 });
        setSelectedObjectId(null);
        setRecipients([]);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <Paper
                sx={{
                    width: '80%', mb: 2,
                    boxShadow: (theme) => `0px 0px 8px 0px rgba(0,0,0,${theme.palette.dark === 'dark' ? 0.75 : 0.25})`,
                    WebkitBoxShadow: (theme) => `0px 0px 8px 0px rgba(0,0,0,${theme.palette.dark === 'dark' ? 0.75 : 0.25})`,
                    bgcolor: (theme) => theme.palette.paper.background,
                }}
                elevation={3}
            >
                <Box sx={{ minHeight: '80vh', backgroundColor: '#f5f5f5', padding: 3 }}>
                    <Typography variant='h5'>Sube un archivo PDF, agrega remitentes y ajusta los recuadros</Typography>

                    <Box sx={{ marginTop: 2 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm:4, md:6 }}>
                                <FormGroup
                                    style={{ height: '30px', display: 'flex' }}
                                >
                                    <TextField
                                        name='fileToSing'
                                        size="small"
                                        fullWidth
                                        type="text"
                                        label="Seleccione..."
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        aria-readonly={true}
                                        value={pdfFile?.name}
                                        InputProps={{
                                            endAdornment: (
                                                <Tooltip title={'Seleccionar archivo'}>
                                                    <FilePresent style={{ fontSize: 25, color: 'gray' }} />
                                                </Tooltip>
                                            ),
                                        }}
                                        onClick={handleFileClick}
                                    />
                                    <input
                                        accept=".pdf"
                                        style={{ display: 'none' }}
                                        id="raised-button-file"
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                    />
                                </FormGroup>
                            </Grid>
                            <Grid size={{ xs: 12, sm:4, md:6 }}>
                                <Tooltip title="Agregar Remitentes">
                                    <IconButton
                                        aria-label="add"
                                        onClick={() => setOpen(true)}
                                        sx={{ color: 'blue' }}
                                    >
                                        <PersonAdd />
                                    </IconButton>
                                </Tooltip>

                                <ModalAddReceiver
                                    open={open}
                                    onClose={onClose}
                                    onSave={OnSetRecipientsAndSelect}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} sx={{ marginTop: "20px" }}>
                        <Box sx={{ display: "flex", justifyContent: "center", margin: "15px 0" }}>
                            <Box
                                className='pdfAreaStyle'
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "200px",
                                    border: "1px solid gray",
                                    padding: "10px",
                                    marginRight: "20px",
                                    opacity: (recipients.length > 0 && pdfFile !== null) ? 1 : 0.5,
                                    pointerEvents: (recipients.length > 0 && pdfFile !== null) ? "auto" : "none"
                                }}>
                                <Grid size={{xs:12, sm: 5}}>
                                    <TextField
                                        id="IdRecipients"
                                        name="InputRecipients"
                                        select
                                        fullWidth
                                        label="Selecciona un remitente"
                                        variant="outlined"
                                        value={recipients.length > 0 ? selectedRecipient : ""}
                                        onChange={(e) => setSelectedRecipient(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value="" disabled sx={{ fontWeight: 'bold' }}>
                                            select an option
                                        </MenuItem>

                                        {recipients.map((item, index) => (
                                            <MenuItem key={index} value={item.email}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Typography variant='h6' sx={{ marginTop: 2 }}>Opciones:</Typography>
                                {["Firma", "Nombre", "Sello"].map((type) => {
                                    return (
                                        <Card
                                            key={type}
                                            className='cardOptionStyle'
                                            draggable
                                            onDragStart={() => handleDragStart(type)}
                                        >
                                            <CardContent
                                                className="CardContent"
                                            >
                                                {GetIcon(type)}
                                                <Typography className="CardTypography">{type}</Typography>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                                {selectedObjectId && (
                                    <Button
                                        onClick={handleDeleteBox}
                                        variant='contained'
                                        color='primary'
                                    >
                                        Eliminar
                                    </Button>
                                )}
                            </Box>

                            <Box
                                className='pdfAreaStyle'
                                sx={{
                                    width: "800px", // Ancho fijo
                                    height: "600px", // Altura fija
                                    overflow: "auto", // Activar scroll horizontal y vertical
                                    border: "1px solid gray", // Opcional: para ver los bordes
                                    position: "relative", // Mantener elementos posicionados correctamente
                                    opacity: (recipients.length > 0 && pdfFile !== null) ? 1 : 0.5,
                                    pointerEvents: (recipients.length > 0 && pdfFile !== null) ? "auto" : "none"
                                }}
                            >
                                {backgroundImages.length > 0 ? (
                                    backgroundImages.map((bg, index) => (
                                        <>
                                            <Box
                                                key={index}
                                                sx={{
                                                    position: "relative",
                                                    width: `${bg.viewport.width}px`, // Usa el tamaño del viewport del PDF
                                                    height: `${bg.viewport.height}px`,
                                                    margin: "0 auto", // Centrar contenido
                                                    textAlign: "center",
                                                    border: "2px solid black",
                                                }}
                                                onDrop={(event) => handleDrop(event, index)}
                                                onDragOver={handleDragOver}
                                            >
                                                <img
                                                    src={bg.url_page}
                                                    alt={`page-${index}`}
                                                    style={{ width: "100%", height: "100%", pointerEvents: "none" }}
                                                />
                                                {predesignedObjects
                                                    .filter((obj) => obj.pageIndex === index)
                                                    .map((obj) => {
                                                        return (
                                                            <Card
                                                                key={obj.id_signature}
                                                                className='cardOptionStyle'
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: `${obj.pixel_y}px`,
                                                                    left: `${obj.pixel_x}px`,
                                                                    width: `${obj.width}px`,
                                                                    height: `${obj.height}px`,
                                                                }}
                                                                onMouseDown={(event) => {
                                                                    setMouseStart({ x: event.clientX, y: event.clientY });
                                                                    handleDragStart("existing", obj.id_signature);
                                                                }}
                                                            >
                                                                <CardContent
                                                                    className="CardContent"
                                                                >
                                                                    <Box>
                                                                        {GetIcon(obj.type)}
                                                                        <Typography className="CardTypography">{obj.type}</Typography>
                                                                    </Box>
                                                                    <Box
                                                                        className='resizeHandleStyle'
                                                                        onMouseDown={(event) => handleMouseDownResize(obj.id_signature, event)}
                                                                    />
                                                                </CardContent>
                                                            </Card>
                                                        );
                                                    })}
                                            </Box>
                                            <Grid>
                                                <Typography variant="subtitle2" component={'span'}>
                                                    Page {index + 1} de {backgroundImages.length}
                                                </Typography>
                                            </Grid>
                                        </>
                                    ))
                                ) : (
                                    <Box
                                        className='emptyStateStyle'
                                    >
                                        <Typography variant="body2">Sube un archivo PDF para verlo aquí</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button
                                variant='contained'
                                color='success'
                                onClick={handleFinalize}
                                sx={{ mx: 3 }}
                            >
                                Guardar
                            </Button>
                            <Button
                                variant='contained'
                                color='success'
                                onClick={handleEmail}
                            >
                                Enviar
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    )
}